/**
 * businessAi.js
 *
 * Pure helpers for the Happy AI business features (digest, alerts, goals).
 * No React, no network — unit-tested in `app/tests/businessAi.test.js`.
 *
 * @module helpers/businessAi
 */

/** Deltas whose magnitude is below this many percent are shown as "flat". */
export const FLAT_DELTA_THRESHOLD = 0.5;

/** Goal metrics the backend accepts. */
export const GOAL_METRICS = ['revenue', 'orders', 'avgCheck'];

/** Goal periods the backend accepts. */
export const GOAL_PERIODS = ['week', 'month', 'custom'];

/** Alert status filters, in display order. */
export const ALERT_STATUS_FILTERS = ['open', 'acknowledged', 'dismissed', 'all'];

/**
 * Classify a percentage delta.
 * @param {*} deltaPct raw delta in percent (`null` when there is no baseline).
 * @param {{invert?: boolean}} [options] `invert` for metrics where lower is better.
 * @returns {{direction: 'up'|'down'|'flat'|'none', tone: 'success'|'danger'|'neutral'}} direction and display tone.
 */
export const classifyDelta = (deltaPct, {invert = false} = {}) => {
    const value = Number(deltaPct);

    if (deltaPct === null || deltaPct === undefined || deltaPct === '' || !Number.isFinite(value)) {
        return {direction: 'none', tone: 'neutral'};
    }
    if (Math.abs(value) < FLAT_DELTA_THRESHOLD) {
        return {direction: 'flat', tone: 'neutral'};
    }
    const up = value > 0;
    const good = invert ? !up : up;

    return {direction: up ? 'up' : 'down', tone: good ? 'success' : 'danger'};
};

/**
 * Format a delta as `+12.3%` / `-4%` / `0%`. Returns `null` without a number.
 * @param {*} deltaPct raw delta in percent.
 * @returns {?string} signed percent text.
 */
export const formatDeltaPct = (deltaPct) => {
    const value = Number(deltaPct);

    if (deltaPct === null || deltaPct === undefined || deltaPct === '' || !Number.isFinite(value)) {
        return null;
    }
    const rounded = Math.round(value * 10) / 10;

    return `${rounded > 0 ? '+' : ''}${rounded}%`;
};

/**
 * Group an integer with thin spaces (Hermes ships no reliable Intl).
 * @param {number} value number to format.
 * @param {number} [maxDecimals=0] fraction digits kept.
 * @returns {string} grouped number.
 */
export const formatNumber = (value, maxDecimals = 0) => {
    const num = Number(value);

    if (!Number.isFinite(num)) return '—';
    const fixed = num.toFixed(maxDecimals);
    const [intPart, frac] = fixed.split('.');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const trimmedFrac = frac ? frac.replace(/0+$/, '') : '';

    return trimmedFrac ? `${grouped},${trimmedFrac}` : grouped;
};

/**
 * KPI / goal value + unit, e.g. `1 234 567 Ft`, `42`, `12,5%`.
 * @param {*} value raw value.
 * @param {?string} [unit] unit string from the backend (`''`/`null` for plain counts).
 * @returns {string} display text, `—` when the value is not a number.
 */
export const formatKpiValue = (value, unit) => {
    if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) {
        return '—';
    }
    const num = Number(value);
    const text = formatNumber(num, Math.abs(num) < 100 && !Number.isInteger(num) ? 1 : 0);

    if (!unit) return text;

    return unit === '%' ? `${text}%` : `${text} ${unit}`;
};

/**
 * Local `YYYY-MM-DD` key of a date (device timezone).
 * @param {Date} [date] the date, default now.
 * @returns {string} date key.
 */
export const toDateKey = (date = new Date()) => {
    const pad = (n) => String(n).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Alert severity → display metadata.
 * @param {string} severity `info` | `warning` | `critical`.
 * @returns {{key: string, tone: string, icon: string, color: string, rank: number}} meta (unknown → info).
 */
export const getSeverityMeta = (severity) => {
    switch (severity) {
        case 'critical':
            return {key: 'critical', tone: 'danger', icon: 'alert-octagon', color: '#EF4444', rank: 0};
        case 'warning':
            return {key: 'warning', tone: 'warning', icon: 'alert', color: '#F97316', rank: 1};
        default:
            return {key: 'info', tone: 'info', icon: 'information', color: '#3B82F6', rank: 2};
    }
};

/**
 * Goal progress ratio clamped to 0..1 for a progress bar.
 * @param {*} pct progress in percent (may exceed 100).
 * @returns {number} 0..1.
 */
export const goalBarRatio = (pct) => {
    const value = Number(pct);

    if (!Number.isFinite(value)) return 0;

    return Math.max(0, Math.min(1, value / 100));
};

/**
 * Pick the top N active goals for the dashboard (lowest completion first would
 * hide wins, so: most advanced unfinished first).
 * @param {Array<object>} goals goals with progress.
 * @param {number} [limit=2] max goals.
 * @returns {Array<object>} the picked goals.
 */
export const pickTopGoals = (goals, limit = 2) => (Array.isArray(goals) ? goals : [])
    .filter((goal) => goal && (goal.status || 'active') === 'active')
    .sort((a, b) => (Number(b.pct) || 0) - (Number(a.pct) || 0))
    .slice(0, limit);

/**
 * Map a push payload (`{screen: 'BusinessDigest'|'BusinessAlerts', digestId?, alertId?}`)
 * to a navigation target. Accepts the `…Screen` suffix and any casing.
 * @param {?object} data push `data`/metadata.
 * @returns {?{name: string, params: object}} target, or `null` when not a business-AI push.
 */
export const resolveBusinessAiRoute = (data) => {
    const screen = String(data?.screen || data?.targetScreen || '').replace(/[^a-z]/gi, '').toLowerCase();
    const digestId = data?.digestId ? String(data.digestId) : '';
    const alertId = data?.alertId ? String(data.alertId) : '';

    if (screen === 'businessdigest' || screen === 'businessdigestscreen') {
        return {name: 'BusinessDigestScreen', params: digestId ? {digestId} : {}};
    }
    if (screen === 'businessalerts' || screen === 'businessalertsscreen') {
        return {name: 'BusinessAlertsScreen', params: alertId ? {alertId} : {}};
    }

    return null;
};
